import * as RadixDialog from '@radix-ui/react-dialog';
import { useRef, type ReactNode } from 'react';
import { Banner, Button, Icon } from '../components';

interface Entry {
  name: ReactNode;
  meta?: ReactNode;
}

/** Confirms an irreversible action by listing every record it touches, by name. Holds focus while it asks and gives it back on close. */
export function ConfirmDialog({
  open = true,
  title,
  children,
  keep,
  items,
  itemsLabel,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'critical',
  onConfirm,
  onCancel,
  busy,
  refused,
  restoreFocus = true,
}: {
  open?: boolean;
  title: ReactNode;
  children?: ReactNode;
  keep?: Entry;
  items: Entry[];
  itemsLabel?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'critical' | 'primary';
  onConfirm?: () => void;
  onCancel?: () => void;
  busy?: boolean;
  refused?: string;
  /** Off for callers that move focus themselves once it closes. */
  restoreFocus?: boolean;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<Element | null>(null);

  return (
    <RadixDialog.Root open={open}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="rq-dialog-backdrop">
          <RadixDialog.Content
            className="rq-dialog"
            role="alertdialog"
            aria-modal="true"
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              openerRef.current = document.activeElement;
              cancelRef.current?.focus();
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              // Focus already placed by the caller stays where it is.
              if (!restoreFocus || document.activeElement !== document.body) return;
              const opener = openerRef.current;
              if (opener instanceof HTMLElement && opener !== document.body && opener.isConnected)
                return opener.focus();
              const heading = document.querySelector<HTMLElement>('main h1');
              if (!heading) return;
              heading.tabIndex = -1;
              heading.focus({ preventScroll: true });
            }}
            onEscapeKeyDown={(event) => {
              event.preventDefault();
              if (!busy) onCancel?.();
            }}
            onPointerDownOutside={(event) => event.preventDefault()}
            onInteractOutside={(event) => event.preventDefault()}
          >
            <RadixDialog.Title className="rq-dialog-title text-heading">{title}</RadixDialog.Title>
            <RadixDialog.Description asChild>
              <div className="rq-dialog-body">
                {children}
                {keep ? (
                  <div className="rq-dialog-keep">
                    <span className="rq-dialog-group">
                      <Icon name="circle-check" size={14} />
                      Keep
                    </span>
                    <div className="rq-dialog-item">
                      <span className="rq-dialog-name">{keep.name}</span>
                      {keep.meta ? <span className="rq-dialog-meta">{keep.meta}</span> : null}
                    </div>
                  </div>
                ) : null}
                <div className="rq-dialog-list">
                  <span
                    className={
                      tone === 'critical' ? 'rq-dialog-group is-critical' : 'rq-dialog-group'
                    }
                  >
                    <Icon name={tone === 'critical' ? 'circle-x' : 'circle-check'} size={14} />
                    {itemsLabel || 'Reject'} ({items.length})
                  </span>
                  <ul>
                    {items.map((item, i) => (
                      <li key={i} className="rq-dialog-item">
                        <span className="rq-dialog-name">{item.name}</span>
                        {item.meta ? <span className="rq-dialog-meta">{item.meta}</span> : null}
                      </li>
                    ))}
                  </ul>
                </div>
                {refused ? (
                  <Banner tone="critical" title="The server refused this" reason={refused} />
                ) : null}
              </div>
            </RadixDialog.Description>
            <div className="rq-dialog-foot">
              <button
                ref={cancelRef}
                type="button"
                className="rq-btn rq-btn--quiet rq-btn--md"
                onClick={onCancel}
              >
                <span className="rq-btn-label">{cancelLabel}</span>
              </button>
              <Button
                variant={tone === 'critical' ? 'danger' : 'primary'}
                onClick={() => !busy && onConfirm?.()}
                loading={busy}
              >
                {confirmLabel}
              </Button>
            </div>
          </RadixDialog.Content>
        </RadixDialog.Overlay>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
